import { convertUTCtoLocalTime, formatTimeToShortAMPM } from '@/utils';
import style from './Visits.module.scss';
import { selectedTranscript } from '@/store/selectedTranscript';
import classNames from 'classnames';
import { EditIcon, RemoveIcon } from '@ax/ui';
import useAnalytics from '@/hooks/useAnalytics';
import {
  navigationSlice,
  selectCareCuesEnabled,
  selectIsEdEnabled,
  selectIsProviderAdmin,
  statusHistoryByIdSelector,
  useAppDispatch,
  useAppSelector,
  Visit,
} from '@ax/redux-store';
import { useCallback, useState } from 'react';
import VisitFormModal, { VisitModalData } from './VisitFormModal';
import { useGetAllAppointmentsApi } from '@/hooks/api';
import { Auth } from '@ax/auth';
import { NOTE_STATUS, NotePageSections } from '@ax/constants';
import VisitDeleteModal from './VisitDeleteModal';
import { getPatientName } from '@/utils/patientName.ts';
import StatusIcon from '../StatusIcon/StatusIcon';
import ConfirmationModal from '@/components/ConfirmationModal';
import { recordingState } from '@/store/recordingState.tsx';
import { useSignalEffect } from '@preact/signals-react';

const VisitItem = ({
  uuid,
  patientFirstName,
  patientLastName,
  appointmentStartTime,
  patientAge,
  patientGender,
  chiefComplaint,
  roomNumber,
  noteStatus,
  onClick,
  selected,
  allContentViewed,
  readonly,
  deleted,
}: Visit) => {
  const analytics = useAnalytics();
  const dispatch = useAppDispatch();
  const selectedVisitDate = useAppSelector((state) => state.selectedDate);
  const formattedTime = appointmentStartTime ? formatTimeToShortAMPM(appointmentStartTime) : '';
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowdeleteModal] = useState(false);
  const { refetch: refetchVisitList } = useGetAllAppointmentsApi(
    Auth.currentUser()?.uid,
    selectedVisitDate?.date,
  );

  const isEdEnabled = useAppSelector(selectIsEdEnabled);
  const careCuesEnabled = useAppSelector(selectCareCuesEnabled);
  const isAdmin = useAppSelector(selectIsProviderAdmin);
  const statusHistory = useAppSelector(statusHistoryByIdSelector(uuid));
  const [latestStatusFromHistory] = (statusHistory ?? []).slice(-1);
  const [isRecordingInProgressModalOpen, setIsRecordingInProgressModalOpen] = useState(false);
  const [pendingOperation, setPendingOperation] = useState<'delete' | 'edit' | undefined>(undefined);


  const handleItemClick = () => {
    onClick?.();

    const analyticsProperties = {
      visitDateTime: `${selectedVisitDate?.date}, ${appointmentStartTime}`,
      noteId: uuid,
    };

    analytics.onVisitSelection(analyticsProperties);

    if (!selected) {
      selectedTranscript.value = {};
    }

    if (careCuesEnabled) {
      dispatch(
        navigationSlice.actions.sectionNavigationRequested({
          section: NotePageSections.CareCues,
          noteId: uuid,
        }),
      );
    }
  };

  const handleEditClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();

    if (recordingState.value.recordingInprogress) {
      setPendingOperation('edit');
      setIsRecordingInProgressModalOpen(true);
      return;
    }

    setShowEditModal(true);
  }, []);

  const handleDeleteClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();

    if (recordingState.value.recordingInprogress) {
      setPendingOperation('delete');
      setIsRecordingInProgressModalOpen(true);
      return;
    }

    setShowdeleteModal(true);
  }, []);

  const uploadedToMds = noteStatus === NOTE_STATUS.NOTE_UPLOADED_TO_MDS;
  const uploadedToEhr = noteStatus === NOTE_STATUS.NOTE_UPLOADED_TO_EHR;
  const draftNoteProcessing = noteStatus === NOTE_STATUS.CONTENT_PROCESSING;
  const unSeenNoteContent = noteStatus === NOTE_STATUS.NOTE_READY && allContentViewed === false;
  const draftNoteReady = noteStatus === NOTE_STATUS.NOTE_READY && allContentViewed;

  let ehrStatusTime = '';
  let mdsStatusTime = '';

  if (
    latestStatusFromHistory &&
    latestStatusFromHistory.status === NOTE_STATUS.NOTE_UPLOADED_TO_EHR
  ) {
    ehrStatusTime = formatTimeToShortAMPM(convertUTCtoLocalTime(latestStatusFromHistory.createdAt));
  }

  if (
    latestStatusFromHistory &&
    latestStatusFromHistory.status === NOTE_STATUS.NOTE_UPLOADED_TO_MDS
  ) {
    mdsStatusTime = formatTimeToShortAMPM(convertUTCtoLocalTime(latestStatusFromHistory.createdAt));
  }

  const visitListItemClasses = classNames(style.visit__list__item, {
    [style.visit__list__item__active]: selected,
    [style.visit__list__item__deleted]: deleted,
  });

  const patientName = getPatientName(patientFirstName, patientLastName);

  const modalData: VisitModalData = {
    uuid,
    patientName,
    appointmentStartTime,
    visitDate: selectedVisitDate?.date,
    patientAge,
    patientGender,
    chiefComplaint,
    roomNumber,
  };

  const handlePendingOperation = () => {
    if (pendingOperation === 'delete') {
      setShowdeleteModal(true);
    } else if (pendingOperation === 'edit') {
      setShowEditModal(true);
    }
    setPendingOperation(undefined);
  }

  const closeDeleteModal = () => {
    setShowdeleteModal(false);
  };

  useSignalEffect(() => {
    if (!recordingState.value.recordingInprogress && pendingOperation) {
      handlePendingOperation();
    }
  });

  const handleRecordingInProgressConfirmCancel = () => {
    setIsRecordingInProgressModalOpen(false);
    recordingState.value = { recordingInprogress: false };
  };

  return (
    <>
      <li
        className={visitListItemClasses}
        onClick={handleItemClick}
      >
        {isEdEnabled && <div className="mb-[3px] text-[12px]">{roomNumber || '-'}</div>}
        <>
          <div
            className={`${
              !isEdEnabled ? style.visit__list__item__content : style.visit__list__item__content_ed
            }`}
          >
            {!isEdEnabled && (
              <span className={style.visit__list__item__content__time}>{formattedTime}</span>
            )}
            <div className={style.visit__list__item__content__text}>
              <span
                className={`${style.visit__list__item__content__text_name} ${
                  isEdEnabled ? 'font-semibold' : ''
                }`}
              >
                {patientName}
              </span>
              {isEdEnabled && (
                <div className="flex gap-[5px]">
                  <span>
                    {patientAge ?? ' '}
                    {patientGender ? ',' : ''}
                  </span>
                  <span>{patientGender ?? ' '}</span>
                </div>
              )}
            </div>
            <div className={style.visit__list__item__icons}>
              {uploadedToMds && (
                <div className={isAdmin ? '' : style.visit__list__item__content__icon}>
                  <StatusIcon status={noteStatus} />
                </div>
              )}
              {uploadedToEhr && (
                <div className={isAdmin ? '' : style.visit__list__item__content__icon}>
                  <StatusIcon status={noteStatus} />
                </div>
              )}
              {draftNoteProcessing && (
                <div className={isAdmin ? '' : style.visit__list__item__content__icon}>
                  <StatusIcon status={noteStatus} />
                </div>
              )}
              {draftNoteReady && (
                <div className={isAdmin ? '' : style.visit__list__item__content__icon}>
                  <StatusIcon status={noteStatus} />
                </div>
              )}
              {unSeenNoteContent && (
                <div className={isAdmin ? '' : style.visit__list__item__content__icon}>
                  <StatusIcon status={noteStatus} />
                </div>
              )}
            </div>
            {!readonly && (
              <div className={style.visit__list__item__actions}>
                <span onClick={handleEditClick}>
                  <EditIcon
                    height={11}
                    width={11}
                    viewBox="0 0 11 11"
                    strokeWidth={0}
                  />
                </span>
                <span onClick={handleDeleteClick}>
                  <RemoveIcon
                    height={11}
                    width={11}
                    viewBox="0 0 11 11"
                    strokeWidth={0}
                  />
                </span>
              </div>
            )}
          </div>
        </>
        {isEdEnabled && (
          <div className="mt-[5px] flex justify-between">
            {chiefComplaint || '-'}
            {uploadedToEhr && ehrStatusTime && isAdmin && <span>{ehrStatusTime}</span>}
            {uploadedToMds && mdsStatusTime && isAdmin && <span>{mdsStatusTime}</span>}
          </div>
        )}

        {!isEdEnabled && isAdmin && (
          <div className="mt-[5px] flex justify-end">
            {''}
            {uploadedToEhr && ehrStatusTime && <span>{ehrStatusTime}</span>}
            {uploadedToMds && mdsStatusTime && <span>{mdsStatusTime}</span>}
          </div>
        )}
      </li>
      <VisitFormModal
        modalOpen={showEditModal}
        modalClose={() => setShowEditModal(false)}
        modalData={modalData}
        refetchData={() => {
          refetchVisitList().catch(console.log);
        }}
      />
      <VisitDeleteModal
        modalOpen={showDeleteModal}
        modalClose={closeDeleteModal}
        modalData={modalData}
        refetchData={() => {
          refetchVisitList().catch(console.log);
        }}
      />
      <ConfirmationModal
        modalOpen={isRecordingInProgressModalOpen}
        modalConfirm={() => {
          handleRecordingInProgressConfirmCancel();
        }}
        modalClose={() => {
          setIsRecordingInProgressModalOpen(false);
        }}
      />
    </>
  );
};

export default VisitItem;
